import { useRef, useState } from 'react';
import { useClickAway } from 'react-use';
import * as Popover from '@radix-ui/react-popover';

import { useTranslation } from 'modules/queryBuilder/hooks/useTranslation';
import noop from 'utils/noop';
import { ALIGNMENT } from 'ui/selects/constants';
import { HORIZONTAL_ALIGNMENT, VERTICAL_ALIGNMENT } from 'ui/utility/types';
import { MultiSelect } from 'ui/selectv2/MultiSelect';
import { useDropdownPopoverAlignment } from 'ui/utility/hooks';
import { SelectValueType } from 'ui/selects';
import { Tooltip } from 'ui/tooltip';

import { useMultiSelectState } from './useMultiSelectState';
import {
  ConditionMultiValueWrapper,
  MultiSelectWrapper,
  MultiSelectWithoutDisplayValuesWrapper,
} from './ConditionMultiValue.styles';
import { ConditionMultiValueTheme } from './ConditionMultiValueTheme';
import { getAdditionalMultiValueProps } from './utils/getAdditionalMultiValueProps';
import { ConditionTriggerButton } from '../TriggerButton';
import { ConditionMultiValueContent } from './ConditionMultiValueContent';
import { ConditionMultiValueProps } from '../types';
import { CustomMultiValueInput } from './CustomMultiValueInput';

export const DATA_TEST = {
  content: 'condition-multi-value-content',
};

export const ConditionMultiValue = <TValue extends SelectValueType>({
  errorMsg,
  field = '',
  hasError = false,
  isDisabled = false,
  isLoading = false,
  isMultiInput = false,
  isReadOnly = false,
  onChange = noop,
  onSelectChange = noop,
  onFetch,
  options = [],
  optionsLimit,
  placeholder = '',
  search = '',
  setSearch,
  shouldHideDisplayValues,
  triggerContent,
  value = [],
  valuesLimit = 1,
  isOpenByDefault = false,
  ...props
}: ConditionMultiValueProps<TValue>) => {
  const { t } = useTranslation();
  const outerRef = useRef<HTMLDivElement>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(true);
  const {
    isEditing,
    inputValues,
    baseOptions,
    onSelect,
    hasEmptyValue,
    onMultiSelectUpdate,
    turnOnEditing,
    selectedValues,
  } = useMultiSelectState({
    value,
    onChange,
    onSelectChange,
    valuesLimit,
    options,
    placeholder,
    field,
    isOpenByDefault,
  });
  useClickAway(outerRef, onMultiSelectUpdate, ['mousedown']);
  const additionalProps = getAdditionalMultiValueProps(isMultiInput, setSearch, search);
  const hasEmptySelectOptions = !isMultiInput && baseOptions.length === 0;

  const { popoverVerticalAlignment, popoverHorizontalAlignment } = useDropdownPopoverAlignment(
    ALIGNMENT.LEFT as HORIZONTAL_ALIGNMENT,
    ALIGNMENT.DOWN as VERTICAL_ALIGNMENT,
  );

  if (!isEditing) {
    return (
      <Tooltip tooltipContent={t('storybook.tooltips.noOptions')} isEnabled={hasEmptySelectOptions}>
        <ConditionTriggerButton
          {...props}
          isEmpty={hasEmptyValue}
          isDisabled={isDisabled || hasEmptySelectOptions}
          isReadOnly={isReadOnly}
          hasError={hasError || !!errorMsg}
          onClick={turnOnEditing}
        >
          <ConditionMultiValueContent
            triggerContent={triggerContent}
            isEmpty={hasEmptyValue}
            placeholder={placeholder}
            values={selectedValues}
            valuesLimit={valuesLimit}
          />
        </ConditionTriggerButton>
      </Tooltip>
    );
  }

  return (
    <>
      {shouldHideDisplayValues ? (
        <Popover.Root open={isEditing} onOpenChange={onMultiSelectUpdate}>
          <Popover.Trigger asChild>
            <ConditionTriggerButton
              {...props}
              isEmpty={hasEmptyValue}
              isDisabled={isDisabled || hasEmptySelectOptions}
              isReadOnly={isReadOnly}
              hasError={hasError || !!errorMsg}
              onClick={turnOnEditing}
            >
              {triggerContent}
            </ConditionTriggerButton>
          </Popover.Trigger>
          <Popover.Portal>
            <Popover.Content
              align={popoverHorizontalAlignment}
              avoidCollisions={false}
              data-test={DATA_TEST.content}
              hideWhenDetached
              side={popoverVerticalAlignment}
            >
              <MultiSelectWithoutDisplayValuesWrapper>
                <MultiSelect
                  id="ConditionMultiValue"
                  autoFocus
                  backspaceRemovesValue={false}
                  customTheme={ConditionMultiValueTheme({
                    isEmpty: hasEmptyValue,
                    shouldHideDisplayValues: true,
                  })}
                  errorMsg={errorMsg}
                  Input={(props) => (
                    <CustomMultiValueInput
                      isMenuOpen={isMenuOpen}
                      search={search}
                      setSearch={setSearch}
                      onMultiSelectUpdate={onMultiSelectUpdate}
                      {...props}
                    />
                  )}
                  isClearable
                  isLoading={isLoading}
                  isMenuOpen={isMenuOpen}
                  optionLimit={optionsLimit}
                  options={baseOptions}
                  onMenuScrollToBottom={onFetch}
                  onSelect={onSelect}
                  placeholder={t('storybook.placeholders.multiSelect')}
                  shouldHideDisplayValues
                  value={inputValues}
                  {...props}
                  {...additionalProps}
                />
              </MultiSelectWithoutDisplayValuesWrapper>
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      ) : (
        <ConditionMultiValueWrapper ref={outerRef}>
          <MultiSelectWrapper>
            <MultiSelect
              id="ConditionMultiValue"
              autoFocus
              customTheme={ConditionMultiValueTheme({ isEmpty: hasEmptyValue })}
              errorMsg={errorMsg}
              Input={(props) => (
                <CustomMultiValueInput
                  isMenuOpen={isMenuOpen}
                  search={search}
                  setSearch={setSearch}
                  onMultiSelectUpdate={onMultiSelectUpdate}
                  {...props}
                />
              )}
              isClearable
              isDefaultMenuOpen
              isLoading={isLoading}
              menuPortalTarget={document.body}
              optionLimit={optionsLimit}
              options={baseOptions}
              onSelect={onSelect}
              onMenuClose={() => setIsMenuOpen(false)}
              onMenuOpen={() => setIsMenuOpen(true)}
              onMenuScrollToBottom={onFetch}
              placeholder={t('storybook.placeholders.multiSelect')}
              value={inputValues}
              {...props}
              {...additionalProps}
            />
          </MultiSelectWrapper>
        </ConditionMultiValueWrapper>
      )}
    </>
  );
};
